/**
 * deliveryFeePolicy.js — pure state-transition logic for the manual booking
 * modal's Delivery ⇄ Pickup toggle. Kept in its own module so Jest can unit
 * test the state machine without needing to render the React tree.
 *
 * Invariant (matches the backend guard in
 *   backend/routes/booking_changes.py::_compute_group_pricing and
 *   backend/pricing.py::compute_payable):
 *
 *     delivery_method === "pickup"  → delivery_fee === 0
 *     delivery_method === "delivery" → delivery_fee === (previous non-zero
 *                                       value  ||  configured default)
 *
 * The frontend enforces the same rule for a snappy UX; the backend enforces
 * it as the source of truth. Belt and suspenders.
 */

/** Configured delivery fee ($) used when we need to seed on Pickup→Delivery. */
export const DEFAULT_DELIVERY_FEE = 15;

/**
 * Compute the next delivery-fee value in response to a method change.
 *
 * @param {"pickup"|"delivery"} nextMethod   New method the admin picked.
 * @param {number|string} prevFee            Current fee state.
 * @param {number} defaultFee                Fallback fee to seed on delivery.
 * @returns {number}                         Next fee to write into state.
 */
export function nextDeliveryFeeOnMethodChange(nextMethod, prevFee, defaultFee = DEFAULT_DELIVERY_FEE) {
  if (nextMethod === "pickup") return 0;
  // switching TO delivery: keep an already-configured value, otherwise seed.
  const num = Number(prevFee);
  if (Number.isFinite(num) && num > 0) return num;
  return Number(defaultFee) > 0 ? Number(defaultFee) : DEFAULT_DELIVERY_FEE;
}
